import { ColorScale } from '@/api/api';
import { Trash2, Plus } from 'lucide-react';

interface ColoringScaleFormProps {
  coloring: ColorScale;
  onChange: (coloring: ColorScale) => void;
  disabled?: boolean;
}

export default function ColoringScaleForm({ coloring, onChange, disabled }: ColoringScaleFormProps) {
  const addColor = () => {
    const newColors = [...coloring.colors, '#000000'];
    onChange({ ...coloring, colors: newColors });
  };

  const removeColor = (index: number) => {
    const newColors = coloring.colors.filter((_, i) => i !== index);
    onChange({ ...coloring, colors: newColors });
  };

  const updateColor = (index: number, value: string) => {
    const newColors = [...coloring.colors];
    newColors[index] = value;
    onChange({ ...coloring, colors: newColors });
  };

  return (
    <div className="space-y-3 bg-orange-50 p-3 rounded">
      <div className="grid grid-cols-2 gap-2">
        <div>
          <label className="text-xs font-medium mb-1 block">Min Value</label>
          <input
            type="number"
            value={coloring.min}
            onChange={(e) => onChange({ ...coloring, min: parseFloat(e.target.value) })}
            disabled={disabled}
            className="w-full px-2 py-1 border rounded text-sm"
            step="any"
          />
        </div>
        <div>
          <label className="text-xs font-medium mb-1 block">Max Value</label>
          <input
            type="number"
            value={coloring.max}
            onChange={(e) => onChange({ ...coloring, max: parseFloat(e.target.value) })}
            disabled={disabled}
            className="w-full px-2 py-1 border rounded text-sm"
            step="any"
          />
        </div>
      </div>

      <div>
        <div className="flex justify-between items-center mb-2">
          <label className="text-xs font-medium">Gradient Colors</label>
          <button
            type="button"
            onClick={addColor}
            disabled={disabled}
            className="flex items-center gap-1 px-2 py-1 text-xs bg-orange-600 text-white rounded hover:bg-orange-700 disabled:opacity-50"
          >
            <Plus className="w-3 h-3" />
            Add Color
          </button>
        </div>

        <div className="flex gap-2 flex-wrap">
          {coloring.colors.map((color, index) => (
            <div key={index} className="flex items-center gap-1 bg-white p-1 rounded border">
              <input
                type="color"
                value={color}
                onChange={(e) => updateColor(index, e.target.value)}
                disabled={disabled}
                className="w-12 h-8 px-1 py-1 border rounded"
              />
              <button
                type="button"
                onClick={() => removeColor(index)}
                disabled={disabled || coloring.colors.length <= 1}
                className="px-1 text-red-600 hover:bg-red-50 rounded disabled:opacity-50"
                title={coloring.colors.length <= 1 ? "At least 1 color required" : "Remove color"}
              >
                <Trash2 className="w-3 h-3" />
              </button>
            </div>
          ))}
        </div>
        <p className="text-xs text-gray-500 mt-1">
          Gradient flows from first color (min) to last color (max)
        </p>
      </div>

      <div className="grid grid-cols-2 gap-2">
        <div>
          <label className="text-xs font-medium mb-1 block">Min Legend Label</label>
          <input
            type="text"
            value={coloring.legend.minLabel}
            onChange={(e) => onChange({
              ...coloring,
              legend: { ...coloring.legend, minLabel: e.target.value }
            })}
            disabled={disabled}
            className="w-full px-2 py-1 border rounded text-sm"
            placeholder="Min label"
          />
        </div>
        <div>
          <label className="text-xs font-medium mb-1 block">Max Legend Label</label>
          <input
            type="text"
            value={coloring.legend.maxLabel}
            onChange={(e) => onChange({
              ...coloring,
              legend: { ...coloring.legend, maxLabel: e.target.value }
            })}
            disabled={disabled}
            className="w-full px-2 py-1 border rounded text-sm"
            placeholder="Max label"
          />
        </div>
      </div>
    </div>
  );
}
